import { DocumentNode, gql } from '@apollo/client';
import {
  Button,
  Center,
  GridItem,
  Image,
  Stack,
  Text,
  VStack,
} from '@chakra-ui/react';
import dayjs from 'dayjs';
import NextLink from 'next/link';
import * as React from 'react';

import { DeprecatedDialInInfo } from '@/components/DialInInfo';
import { EventTime } from '@/components/EventTime';
import { VenueFragment } from '@/fragments/venue-fragment';
import { useCurrent } from '@/hooks/useCurrent';
import { ImageFallback } from '@/imports/ui/chakra/components/ImageFallback';
import { SpeakerCard } from '@/imports/ui/components/SpeakerCard';
import { ResourceCard } from '@/imports/ui/pages/conference/pages/conference-video-pages/components/SharedResourcesCard/components/ResourceCard';

import { ConferenceSessionMobileInfo_SessionFragment } from './graphql-types/ConferenceSessionMobileInfo_SessionFragment';

const INCREMENT_NUM = 3;

export const ConferenceSessionMobileInfo: React.FC<{
  conferenceSession?: ConferenceSessionMobileInfo_SessionFragment;
}> & { sessionFragment: DocumentNode } = ({ conferenceSession }) => {
  const current = useCurrent();
  const speakerIds = !!conferenceSession?.speakers
    ? conferenceSession.speakers.map((speaker) => speaker.user._id)
    : [];
  const isCurrentUserSpeaker =
    !!current.user && speakerIds.includes(current.user._id);

  const speakers = conferenceSession?.speakers ?? null;

  const [numfilesToShow, setNumfilesToShow] = React.useState(INCREMENT_NUM);

  if (!conferenceSession) {
    return null;
  }

  const allResources = conferenceSession.resources.results ?? null;
  const resourcesCount = allResources?.length ?? 0;

  const dialInProps =
    conferenceSession.venue.online &&
    conferenceSession.venue.online.__typename ===
      'EventVenue_TradewingPlatform' &&
    conferenceSession.venue.online.stream.__typename ===
      'EventVenue_TradewingPlatform_Stream_Live' &&
    conferenceSession.venue.online.stream.virtualEvent
      ? {
          dialInCode:
            conferenceSession.venue.online.stream.virtualEvent.dialInCode,
          listenerDialInCode:
            conferenceSession.venue.online.stream.virtualEvent
              .listenerDialInCode,
          dialInEnabled:
            conferenceSession.venue.online.stream.virtualEvent.dialInEnabled,
          dialInPhoneNumber:
            conferenceSession.venue.online.stream.virtualEvent
              .dialInPhoneNumber,
          isCurrentUserSpeaker: isCurrentUserSpeaker,
          isParticipationAllowed:
            conferenceSession.venue.online.stream.virtualEvent.maxParticipants >
            0,
        }
      : null;

  return (
    <VStack
      overflowY="scroll"
      sx={{
        '&::-webkit-scrollbar': {
          display: 'none',
        },
        '&::-webkit-scrollbar-thumb': {
          display: 'none',
        },
      }}
    >
      <VStack shadow="md" spacing="1" w="full">
        <Image
          w="full"
          h="auto"
          zIndex="overlay"
          src={conferenceSession.image?.imageProxy || ''}
          alt="event banner"
          fallback={<ImageFallback w="full" aspectRatio="8/3" bg="gray.200" />}
        />

        <Stack w="full" p="4">
          <Text>
            <EventTime
              startDate={conferenceSession.startDate}
              endDate={conferenceSession.endDate}
            />
          </Text>
        </Stack>
      </VStack>

      {speakers?.length && (
        <VStack alignItems="flex-start" p="4" w="full" shadow="md" spacing="2">
          <Text fontWeight="semibold">
            {speakers.length > 1 ? 'Speakers' : 'Speaker'}
          </Text>

          {speakers
            .filter((speaker) => !speaker.isHidden)
            .map((speaker) => (
              <NextLink
                href={`/members/${speaker.user._id}`}
                key={speaker.user._id}
              >
                <GridItem>
                  <SpeakerCard user={speaker.user} />
                </GridItem>
              </NextLink>
            ))}
        </VStack>
      )}

      {conferenceSession.description !== null && (
        <VStack alignItems="flex-start" p="4" w="full" shadow="md">
          <Text fontWeight="semibold">Description</Text>
          <Text noOfLines={5} color="gray.600" textStyle="compact">
            {conferenceSession.description}
          </Text>
        </VStack>
      )}

      {conferenceSession.venue.online &&
        conferenceSession.venue.online.__typename ===
          'EventVenue_TradewingPlatform' &&
        conferenceSession.venue.online.stream.__typename ===
          'EventVenue_TradewingPlatform_Stream_Live' &&
        conferenceSession.venue.online.stream.virtualEvent &&
        conferenceSession.venue.online.stream.virtualEvent.dialInEnabled &&
        dayjs().isSameOrBefore(conferenceSession.endDate) && (
          <VStack
            spacing="4"
            w="full"
            justifyContent="flex-start"
            alignItems="flex-start"
            shadow="md"
            p="4"
          >
            <DeprecatedDialInInfo {...dialInProps} />
          </VStack>
        )}

      {resourcesCount > 0 && (
        <VStack
          spacing="4"
          w="full"
          justifyContent="flex-start"
          alignItems="flex-start"
          p="4"
        >
          <Text fontWeight="semibold">Resources</Text>
          {allResources?.slice(0, numfilesToShow).map((resource) => {
            return (
              <ResourceCard
                key={resource._id}
                resource={resource}
                canUserEdit={false}
                onRemoveResource={null}
                isRemoving={false}
              />
            );
          })}

          {numfilesToShow < resourcesCount && (
            <Center py="4" w="full">
              <Button
                variant="link"
                colorScheme="black"
                onClick={() =>
                  setNumfilesToShow(
                    (numfilesToShow) => numfilesToShow + INCREMENT_NUM
                  )
                }
              >
                See More Documents
              </Button>
            </Center>
          )}
        </VStack>
      )}
    </VStack>
  );
};

ConferenceSessionMobileInfo.sessionFragment = gql`
  fragment ConferenceSessionMobileInfo_SessionFragment on ConferenceSession {
    _id
    title
    description
    image {
      _id
      imageProxy(input: { fit: "crop", ar: "8:3", w: 400, fm: "png" })
    }
    venue {
      ...VenueFragment
    }
    speakers {
      user {
        _id
        ...SpeakerCard_UserFragment
      }
      isHidden
    }
    startDate
    endDate
    canCurrentUserEditResources
    resources(input: { offset: 0, limit: 100 }) {
      results {
        _id
        authorId
        dateCreated
        resourceSpec {
          ... on EventResource_Spec_Document {
            file {
              name
              downloadUrl
              contentType
            }
          }
        }
      }
      totalCount
    }
  }
  ${SpeakerCard.userFragment}
  ${VenueFragment}
`;
